import * as React from 'react';
import { st, classes } from './WeeklyDatePicker.st.css';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import {
  WeekDatesRange,
  WeeklyDatePickerProps,
  DatePickerFirstDayOfWeek,
  DatePickerLocale,
} from './types';
import {
  getWeekDatesRange,
  WeeklyDateNavigation,
} from '../WeeklyDateNavigation';
import { getWeek, isSameDay, addDays, isPast, endOfDay } from 'date-fns';
import {
  DayPicker,
  RowProps,
  Row,
  ClassNames,
  DayProps,
  useDayRender,
  Button,
  DateFormatter,
  WeekdayLabel,
  CaptionProps,
} from 'react-day-picker-v8';
import {
  formatDateForDataAttr,
  formatDay,
  formatLongWeekDayName,
  formatNarrowWeekDayName,
  formatShortWeekDayName,
  getFirstAvailableDate,
} from './util/dateUtils';
import {
  areDatesInTheSameWeek,
  getCaptionText,
  getOffsetWeekDatesRange,
} from '../WeeklyDateNavigation/dateUtils/dateUtils';
import {
  useWeeklyDatePickerContext,
  WeeklyDatePickerProvider,
} from './WeeklyDatePickerProvider';
import { KEYS } from '../../common/keyCodes';
import { IconButton } from '../IconButton';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { Text } from '../Text';
import { shellClasses } from '../../common/shellClasses';

/** A weekly Date Picker to select a specific date. */
const CaptionElement = ({
  rtl,
  mobile,
  leftArrowAriaLabel,
  leftArrowAriaLabelledBy,
  rightArrowAriaLabel,
  rightArrowAriaLabelledBy,
  locale,
  firstDayOfWeek,
  removeOuterPadding,
}) => {
  const Caption = (props: CaptionProps) => {
    const { currentWeek, setCurrentMonth, setCurrentWeek } =
      useWeeklyDatePickerContext();

    const onChange = (week: WeekDatesRange) => {
      setCurrentWeek(week);
      setCurrentMonth(week.startOfWeek);
    };

    return (
      <WeeklyDateNavigation
        captionId={props.id}
        className={st(classes.header, {
          removeOuterPadding,
          isMobile: mobile,
          rtl,
        })}
        value={currentWeek}
        onChange={onChange}
        prevArrowAriaLabel={leftArrowAriaLabel}
        prevArrowAriaLabelledBy={leftArrowAriaLabelledBy}
        nextArrowAriaLabel={rightArrowAriaLabel}
        nextArrowAriaLabelledBy={rightArrowAriaLabelledBy}
        locale={locale}
        firstDayOfWeek={firstDayOfWeek}
      />
    );
  };

  return Caption;
};

const WeekView = ({
  firstDayOfWeek,
}: {
  firstDayOfWeek: DatePickerFirstDayOfWeek;
}) => {
  const Week = (propsRow: RowProps) => {
    const { currentWeek } = useWeeklyDatePickerContext();
    const weekNumber = getWeek(currentWeek.startOfWeek, {
      weekStartsOn: firstDayOfWeek,
    });

    if (!(weekNumber === propsRow.weekNumber)) {
      return <></>;
    }

    return <Row {...propsRow} />;
  };

  return Week;
};

const CustomDay = ({
  dateIndication,
  locale,
  firstAvailableDate,
  groupWeekdaysAndDates,
  isMobile,
}: {
  dateIndication: WeeklyDatePickerProps['dateIndication'];
  locale: DatePickerLocale;
  firstAvailableDate: Date;
  groupWeekdaysAndDates: boolean;
  isMobile: boolean;
}) => {
  const Day = (propsDay: DayProps) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const dayRender = useDayRender(
      propsDay.date,
      propsDay.displayMonth,
      buttonRef,
    );
    const day = propsDay.date;
    const isSelected = isSameDay(day, dayRender.selectedDays as Date);
    const dateIndicationNode =
      dateIndication &&
      dateIndication({
        date: day,
        isSelected,
      });
    const buttonProps = { ...dayRender.buttonProps, tabIndex: 0 };

    const isFirstAvailableDate = isSameDay(firstAvailableDate, day);

    const calculateTabIndex = () => {
      if (isFirstAvailableDate) {
        return 0;
      }
      return -1;
    };

    return (
      <Button
        {...buttonProps}
        ref={buttonRef}
        data-date={formatDateForDataAttr(day)}
        tabIndex={calculateTabIndex()}
      >
        <div
          className={st(classes.dayWrapper, {
            hasIndication: !!dateIndicationNode,
          })}
        >
          {groupWeekdaysAndDates && (
            <span aria-hidden="true">
              {isMobile
                ? formatNarrowWeekDayName(day, locale)
                : formatShortWeekDayName(day, locale)}
            </span>
          )}
          <span aria-hidden="true">{day.getDate()}</span>
          <span className={st(classes.vhidden)}>{formatDay(day, locale)}</span>
          {dateIndicationNode && !groupWeekdaysAndDates ? (
            <div className={classes.dayIndicationContainer}>
              {dateIndicationNode}
            </div>
          ) : null}
        </div>
      </Button>
    );
  };

  return Day;
};

const WeeklyDatePickerContent: React.FunctionComponent<
  WeeklyDatePickerProps
> = ({
  locale = 'en',
  excludePastDates = false,
  filterDate = () => true,
  showMonthDropdown = false,
  showYearDropdown = false,
  dateIndication = () => null,
  className,
  value,
  onChange,
  today,
  firstDayOfWeek = 1,
  'data-hook': dataHook,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  leftArrowAriaLabel,
  leftArrowAriaLabelledBy,
  rightArrowAriaLabel,
  rightArrowAriaLabelledBy,
  removeOuterPadding,
  arrowLocation = 'caption',
  groupWeekdaysAndDates = false,
}: WeeklyDatePickerProps) => {
  const { rtl, mobile } = useTPAComponentsContext();
  const { currentMonth, currentWeek, setCurrentMonth, setCurrentWeek } =
    useWeeklyDatePickerContext();
  const [prevValue, setPrevValue] = React.useState<Date>(value);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const WeeklyView = React.useCallback(WeekView({ firstDayOfWeek }), [
    firstDayOfWeek,
  ]);

  const firstAvailableDate = React.useMemo(
    () =>
      getFirstAvailableDate({
        startOfWeekDate: currentWeek.startOfWeek,
        filterDate,
        excludePastDates,
      }),
    [currentWeek.startOfWeek.getDate(), excludePastDates, filterDate],
  );

  const Day = React.useCallback(
    CustomDay({
      dateIndication,
      locale,
      firstAvailableDate,
      groupWeekdaysAndDates,
      isMobile: mobile,
    }),
    [dateIndication, firstAvailableDate, firstDayOfWeek, locale],
  );
  const Caption = React.useCallback(
    CaptionElement({
      rtl,
      mobile,
      leftArrowAriaLabelledBy,
      leftArrowAriaLabel,
      rightArrowAriaLabel,
      rightArrowAriaLabelledBy,
      locale,
      firstDayOfWeek,
      removeOuterPadding,
    }),
    [
      rtl,
      mobile,
      leftArrowAriaLabelledBy,
      leftArrowAriaLabel,
      rightArrowAriaLabel,
      rightArrowAriaLabelledBy,
      locale,
      firstDayOfWeek,
      removeOuterPadding,
    ],
  );

  const updateWeekRange = (weekRange?: WeekDatesRange) => {
    if (weekRange) {
      setCurrentWeek(weekRange, () => setCurrentMonth(weekRange.startOfWeek));
    } else {
      if (!isSameDay(value, prevValue)) {
        setPrevValue(value);
        const latestWeek = getWeekDatesRange(value, firstDayOfWeek);
        if (
          !areDatesInTheSameWeek(
            currentWeek.startOfWeek,
            latestWeek.startOfWeek,
            firstDayOfWeek,
          )
        ) {
          setCurrentWeek(latestWeek, () =>
            setCurrentMonth(latestWeek.startOfWeek),
          );
        }
      }
    }
  };

  const focusDate = (date: Date) => {
    const focusEl = containerRef.current.querySelector(
      `[data-date="${formatDateForDataAttr(date)}"]`,
    ) as HTMLElement;
    if (focusEl) {
      focusEl.focus();
    }
  };

  const handleArrowNavigation = React.useCallback(
    (currentDate: Date, offset: number) => {
      const newDate = addDays(currentDate, offset);
      const isEnabled =
        filterDate(newDate) && !(excludePastDates && isPast(endOfDay(newDate)));
      if (!isEnabled) {
        return;
      }
      if (
        !areDatesInTheSameWeek(newDate, currentWeek.startOfWeek, firstDayOfWeek)
      ) {
        const newWeek = getWeekDatesRange(newDate, firstDayOfWeek);
        setCurrentWeek(newWeek, () => setCurrentMonth(newWeek.startOfWeek));
      }
      focusDate(newDate);
    },
    [currentWeek, firstDayOfWeek, excludePastDates, filterDate],
  );

  const onDayKeyDownFactory = React.useCallback(
    (currentDate: Date, _modifiers, event: React.KeyboardEvent) => {
      switch (event.key) {
        case KEYS.ArrowLeft:
          event.preventDefault();
          event.stopPropagation();
          rtl
            ? handleArrowNavigation(currentDate, 1)
            : handleArrowNavigation(currentDate, -1);
          break;
        case KEYS.ArrowRight:
          event.preventDefault();
          event.stopPropagation();
          rtl
            ? handleArrowNavigation(currentDate, -1)
            : handleArrowNavigation(currentDate, 1);
          break;
        case KEYS.Enter:
        case KEYS.Spacebar:
          event.preventDefault();
          (event.target as HTMLElement).click();
          focusDate(currentDate);
          break;
        default:
          break;
      }
    },
    [rtl, handleArrowNavigation],
  );

  // this is needed because react-day-picker is expected to locale as date-fns but we are using Intl.
  // the formatter is cutomize in order to support Wix locale base on Intl and not date-fns.
  const formatWeekdayName: DateFormatter = (date) => {
    return formatShortWeekDayName(date, locale);
  };
  const labelWeekday: WeekdayLabel = (date) => {
    return formatLongWeekDayName(date, locale);
  };
  const { root, ...classesWithoutRoot } = classes;
  const classNames: ClassNames = {
    ...classesWithoutRoot,
    table: st(classes.table, { isMobile: mobile }),
    day: st(classes.day, 'DayPicker-Day', shellClasses.hasCustomFocus),
    head: classes.weekdays,
    head_cell: classes.weekday,
  };

  updateWeekRange();

  const weekAnnouncement = `Week starting ${new Intl.DateTimeFormat(locale, {
    weekday: 'long',
  }).format(currentWeek.startOfWeek)}, ${new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
  }).format(currentWeek.startOfWeek)}`;

  return (
    <>
      <div
        ref={containerRef}
        className={st(
          root,
          { arrowLocation, rtl, groupWeekdaysAndDates },
          className,
        )}
        data-hook={dataHook}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        role="region"
      >
        {arrowLocation === 'dates' && (
          <Text className={classes.caption} data-hook={`${dataHook}-caption`}>
            {getCaptionText(currentWeek, locale)}
          </Text>
        )}
        <div className={classes.dayPickerContainer}>
          {arrowLocation === 'dates' && (
            <IconButton
              className={classes.arrow}
              icon={<ChevronLeft />}
              data-hook="prev-arrow"
              onClick={() => {
                const nextRange = getOffsetWeekDatesRange(
                  currentWeek.startOfWeek,
                  -1,
                  {
                    locale,
                    firstDayOfWeek,
                  },
                );
                updateWeekRange(nextRange);
              }}
              aria-labelledby={leftArrowAriaLabelledBy}
              aria-label={leftArrowAriaLabel}
            />
          )}
          <DayPicker
            components={{
              Caption: arrowLocation === 'caption' ? Caption : () => null,
              Day,
              Row: WeeklyView,
              HeadRow: groupWeekdaysAndDates ? () => null : undefined,
            }}
            showOutsideDays
            mode="single"
            selected={value}
            onSelect={onChange}
            className={classes.rdp}
            classNames={classNames}
            captionLayout={
              showMonthDropdown && showYearDropdown ? 'dropdown' : 'buttons'
            }
            weekStartsOn={firstDayOfWeek}
            month={currentMonth}
            onDayKeyDown={onDayKeyDownFactory}
            disabled={[
              (date) => !filterDate(new Date(date)),
              ...(excludePastDates ? [{ before: new Date() }] : []),
            ]}
            today={today}
            formatters={{ formatWeekdayName }}
            labels={{ labelWeekday }}
            required
          />
          {arrowLocation === 'dates' && (
            <IconButton
              className={classes.arrow}
              icon={<ChevronRight />}
              data-hook="next-arrow"
              onClick={() => {
                const nextRange = getOffsetWeekDatesRange(
                  currentWeek.startOfWeek,
                  1,
                  {
                    locale,
                    firstDayOfWeek,
                  },
                );
                updateWeekRange(nextRange);
              }}
              aria-labelledby={rightArrowAriaLabelledBy}
              aria-label={rightArrowAriaLabel}
            />
          )}
        </div>
      </div>
      <span
        aria-live="polite"
        aria-atomic="true"
        className={st(classes.vhidden)}
      >
        {weekAnnouncement}
      </span>
    </>
  );
};

export const WeeklyDatePicker: React.FunctionComponent<
  WeeklyDatePickerProps
> = ({ value = new Date(), ...restProps }) => (
  <WeeklyDatePickerProvider
    firstDayOfWeek={restProps.firstDayOfWeek}
    onDisplayedWeekChange={restProps.onDisplayedWeekChange}
    selectedDate={value}
  >
    <WeeklyDatePickerContent {...restProps} value={value} />
  </WeeklyDatePickerProvider>
);

WeeklyDatePicker.displayName = 'WeeklyDatePicker';
